import { Link } from 'react-router-dom';
import { Building2, Home, Warehouse, ArrowRight, CheckCircle2 } from 'lucide-react';
import VideoHero from '../components/VideoHero';

const VALUES = [
  { title: 'Professionalism', text: 'Every interaction and service is delivered to the highest standard, reflecting our commitment to quality in everything we do.' },
  { title: 'Accountability', text: 'We take ownership of the assets and communities under our care, and stand behind every commitment we make.' },
  { title: 'Transparency', text: 'Open, honest and clear communication with property owners, investors and residents at every step.' },
  { title: 'Excellence', text: 'We pursue continuous improvement so every estate under our management sets a benchmark worth following.' },
];

const MANAGED_PROPS = [
  { icon: <Home className="h-8 w-8 text-primary" />, title: 'Residential Estates', text: 'Gated communities and residential developments managed with care, security and consistency.' },
  { icon: <Building2 className="h-8 w-8 text-primary" />, title: 'Commercial Developments', text: 'Offices, retail and mixed-use schemes kept efficient, operational and always presentable.' },
  { icon: <Warehouse className="h-8 w-8 text-primary" />, title: 'Mixed-Use Properties', text: 'Complex developments combining residential and commercial uses, managed as one.' },
];

const JOURNEY_MILESTONES = [
  { title: 'Founded', text: 'Naya Edwin Solutions was founded with a clear vision: to provide trusted real estate and professional facility management services built on integrity, excellence and long-term value.' },
  { title: 'Real Estate Growth', text: 'We are expanding our real estate services to provide clients with carefully vetted properties, trusted guidance and seamless transactions—creating opportunities for confident property ownership and investment.' },
  { title: 'Facility Management Excellence', text: 'We are expanding our facility management services to deliver reliable, proactive and professional care for residential and commercial properties—ensuring every asset remains secure, compliant, efficient and well maintained.' },
  { title: 'Today', text: 'We continue to grow as a trusted real estate and facility management company, delivering professional solutions that create value, protect assets and build lasting relationships with our clients.' },
];

export default function About() {
  return (
    <div>
      {/* Hero Section */}
      <VideoHero height="h-screen" overlay="bg-black bg-opacity-20" videoKey="hero_video_about">
        <div className="max-w-7xl mx-auto px-4 h-full flex items-start pt-[33vh]">
          <div className="text-white max-w-3xl">
            <p className="uppercase tracking-[0.35em] text-primary text-xs md:text-sm font-semibold mb-4">About Us</p>
            <h1 className="font-display text-5xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4 capitalize">
              About Naya Edwin Solutions
            </h1>
            <p className="uppercase tracking-[0.35em] text-white text-xs md:text-sm font-semibold">
              Your Trusted Real Estate &amp; Facility Management Partner
            </p>
          </div>
        </div>
      </VideoHero>

      {/* Company Overview */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div className="space-y-6">
            <p className="uppercase tracking-[0.3em] text-obsidian text-sm font-semibold">Who We Are</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian leading-tight capitalize">
              A professional real estate &amp; facility management company
            </h2>
            <p className="text-charcoal leading-relaxed">
              <strong className="font-bold uppercase text-[17.33px]">Naya Edwin Solutions</strong> is a professional real estate and facility management company
              committed to delivering exceptional property solutions and creating lasting value for our clients.
            </p>
            <p className="text-charcoal leading-relaxed">
              Our real estate services span property sales, leasing, and property management, connecting clients
              with the right opportunities while ensuring a seamless and transparent experience.
            </p>
            <p className="text-charcoal leading-relaxed">
              Through our facility management services, we provide efficient property operations, maintenance
              coordination, vendor management, and essential support services that keep residential and commercial
              properties functional, secure, and well-maintained.
            </p>
            <p className="text-charcoal leading-relaxed">
              Driven by professionalism, integrity, and excellence, we provide end-to-end property solutions that
              protect investments, enhance property value, and deliver peace of mind.
            </p>
            <Link
              to="/management"
              className="inline-flex items-center bg-primary text-black px-7 py-2 font-semibold hover:bg-primary-light transition-colors"
            >
              Explore Our Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 border-2 border-primary/40 rounded-lg transform rotate-1"></div>
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
              alt="Modern building"
              className="relative rounded-lg shadow-2xl w-full h-80 md:h-[420px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-ash-light py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="uppercase tracking-[0.3em] text-obsidian text-sm md:text-base font-semibold mb-3">Our Direction</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian capitalize">Vision &amp; Mission</h2>
            <div className="w-16 h-0.5 bg-primary mx-auto mt-6" />
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            <div className="bg-white border border-gray-100 p-10 rounded-xl shadow-lg">
              <h3 className="text-obsidian text-2xl md:text-3xl font-semibold mb-4 capitalize">Our Vision</h3>
              <p className="text-charcoal leading-relaxed">
                To become Nigeria's leading real estate and facility management company, renowned for
                delivering innovative property solutions, operational excellence, exceptional customer
                service, and sustainable value that enhances properties and protects investments.
              </p>
            </div>
            <div className="bg-white border border-gray-100 p-10 rounded-xl shadow-lg">
              <h3 className="text-obsidian text-2xl md:text-3xl font-semibold mb-4 capitalize">Our Mission</h3>
              <p className="text-charcoal leading-relaxed">
                To provide world-class real estate and facility management services through professional
                expertise, innovative technology, proactive maintenance, and transparent management,
                ensuring safe, efficient, and well-maintained properties while maximizing value for
                property owners, investors, and residents.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="bg-obsidian py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="uppercase tracking-[0.3em] text-primary text-xs font-semibold mb-3">Our Standards</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white capitalize">Core Values</h2>
            <div className="w-16 h-0.5 bg-primary mx-auto mt-6" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((v) => (
              <div
                key={v.title}
                className="bg-charcoal p-8 border-t-4 border-primary hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              >
                <CheckCircle2 className="h-8 w-8 text-primary mb-4" />
                <h3 className="text-2xl md:text-3xl font-semibold mb-3 text-white capitalize">{v.title}</h3>
                <p className="text-ash-light text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="uppercase tracking-[0.3em] text-obsidian text-sm md:text-base font-semibold mb-3">Our Journey</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian capitalize">Milestones on the Way</h2>
            <div className="w-16 h-0.5 bg-primary mx-auto mt-6" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {JOURNEY_MILESTONES.map((m, i) => (
              <div key={m.title} className="bg-ash-light border border-gray-100 p-8 rounded-xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center mb-5">
                  <span className="text-black font-bold text-lg font-display">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-xl md:text-2xl font-semibold mb-3 text-obsidian capitalize">{m.title}</h3>
                <p className="text-charcoal text-sm leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Manage */}
      <section className="bg-ash-light py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="uppercase tracking-[0.3em] text-obsidian text-xs font-semibold mb-3">Our Expertise</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-obsidian capitalize">Our Portfolio</h2>
            <div className="w-16 h-0.5 bg-primary mx-auto mt-6" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {MANAGED_PROPS.map((p) => (
              <div key={p.title} className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-5">
                  {p.icon}
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold mb-3 text-obsidian capitalize">{p.title}</h3>
                <p className="text-charcoal text-sm leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ash-light py-16 border-b border-primary/40">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-obsidian rounded-xl border-t-4 border-primary shadow-2xl overflow-hidden">
            <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-6 px-6 sm:px-10 py-10 md:py-12 text-center sm:text-left">
              <div className="min-w-0">
                <p className="uppercase tracking-[0.3em] text-primary text-xs font-semibold mb-3">Partner With Us</p>
                <h2 className="text-lg md:text-3xl xl:text-4xl font-semibold text-white mb-3 capitalize">
                  Let's Manage Your Property
                </h2>
                <p className="text-white/75 text-sm md:text-base leading-relaxed max-w-2xl">
                  Your property deserves more than routine maintenance. Partner with us to provide you a proactive, professional management that keeps your asset secure, well maintained and efficiently operated, while protecting its long-term value and giving you peace of mind.
                </p>
              </div>
              <div className="w-full sm:w-auto shrink-0">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center w-full sm:w-auto bg-primary text-black px-8 py-3 md:py-3 font-semibold hover:bg-primary-light transition-colors whitespace-nowrap"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
